import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import type { SkuStoreGeo } from "../api/generated/model";
import type { Platform } from "../api/labels";
import { donutClusterIcon } from "./mapCluster";

// Which stores' serviceable-area hulls the coverage toggle draws, per map mode:
// coverage -> every carried store; availability -> only the in-stock stores.
function inCoverage(s: SkuStoreGeo, mode: SkuMapMode): boolean {
  if (!s.boundary || s.boundary.length < 3) return false;
  return mode === "coverage" ? s.found : s.in_latest && s.is_out_of_stock === false;
}

// Two-mode SKU map for the SKU detail page, both built from the same annotated
// store set (active sweep universe + any store that ever carried the SKU):
//   - "coverage"     - where the SKU is carried vs swept-but-never-carried.
//   - "availability" - among the latest sweep, in-stock vs out-of-stock (with
//                      "was carried" / "never carried" as muted context).
// Markers sit at each store's LOCATION (not its routing probe point); their
// colour encodes the coverage/availability category and their SHAPE encodes the
// store's coverage-census anchor role. Both maps share a view sync so panning or
// zooming one drives the other.

export type SkuMapMode = "coverage" | "availability";

type Cat = { color: string; label: string };

const COVERAGE: Record<string, Cat> = {
  carried: { color: "#18a957", label: "Carried" },
  none: { color: "#64748b", label: "Not carried" },
};

const AVAIL: Record<string, Cat> = {
  in_stock: { color: "#18a957", label: "In stock" },
  oos: { color: "#e5484d", label: "Out of stock" },
  listed: { color: "#2f6feb", label: "Listed (stock unknown)" },
  dropped: { color: "#f5a524", label: "Was carried" },
  none: { color: "#64748b", label: "Never carried" },
};

function coverageKey(s: SkuStoreGeo): string {
  return s.found ? "carried" : "none";
}

function availKey(s: SkuStoreGeo): string {
  if (s.in_latest) {
    if (s.is_out_of_stock === true) return "oos";
    if (s.is_out_of_stock === false) return "in_stock";
    return "listed";
  }
  return s.found ? "dropped" : "none";
}

// -- anchor-role -> marker shape --------------------------------------------
// The store's coverage-census anchor role (anchors.py AnchorKind) becomes the
// marker's shape, so the role reads at a glance while colour stays free for the
// coverage/availability status. Unknown/legacy rows fall back to a circle.
type ShapeKind = "circle" | "square" | "triangle" | "diamond" | "star";

const ANCHOR_SHAPE: Record<string, ShapeKind> = {
  exclusive: "star",
  primary: "square",
  least_overlap: "triangle",
  direct: "diamond",
};
const ANCHOR_LABEL: Record<string, string> = {
  exclusive: "Exclusive",
  primary: "Primary",
  least_overlap: "Least overlap",
  direct: "Direct",
};
const ANCHOR_ORDER = ["exclusive", "primary", "least_overlap", "direct"];

function anchorKey(s: SkuStoreGeo): string {
  return (s.anchor_kind || "").trim().toLowerCase();
}
function anchorShape(kind: string): ShapeKind {
  return ANCHOR_SHAPE[kind] ?? "circle";
}

// Inner SVG for a shape drawn in a 0..16 viewBox, centred on (8, 8).
function shapeInner(shape: ShapeKind): string {
  switch (shape) {
    case "square":
      return `<rect x="3.2" y="3.2" width="9.6" height="9.6" rx="1.2" />`;
    case "diamond":
      return `<path d="M8 1.8 L14.2 8 L8 14.2 L1.8 8 Z" />`;
    case "triangle":
      return `<path d="M8 2.2 L14.4 13.6 L1.6 13.6 Z" />`;
    case "star":
      return `<path d="M8 1.4 L9.9 5.9 L14.7 6.3 L11 9.5 L12.2 14.2 L8 11.5 L3.8 14.2 L5 9.5 L1.3 6.3 L6.1 5.9 Z" />`;
    default:
      return `<circle cx="8" cy="8" r="5.4" />`;
  }
}

// A divIcon marker: the shape (anchor role) filled in the category colour, ringed
// white so it stays legible over any basemap. Muted context stores draw smaller.
function shapeIcon(shape: ShapeKind, fill: string, muted: boolean): L.DivIcon {
  const box = muted ? 13 : 17;
  return L.divIcon({
    className: "sku-mk",
    iconSize: [box, box],
    iconAnchor: [box / 2, box / 2],
    html:
      `<svg width="${box}" height="${box}" viewBox="0 0 16 16" ` +
      `fill="${fill}" fill-opacity="${muted ? 0.85 : 1}" ` +
      `stroke="#fff" stroke-width="1.4" stroke-linejoin="round">${shapeInner(shape)}</svg>`,
  });
}

// -- clustering -------------------------------------------------------------
// A sweep universe is thousands of stores, and the page draws two maps from the same
// set, so one divIcon per store means ~5k live DOM nodes and a map that crawls on every
// pan. Markers are clustered instead (same leaflet.markercluster as the stores map) and
// only expand into individual points once zoomed in past the city level.
//
// Colour here carries the answer (carried / in stock / not), so a cluster can't just be
// a count: its ring is a donut of the categories underneath it, in palette order, so a
// region reads as "mostly grey" or "mostly green" without expanding it.
type CatMarker = L.Marker & { _skuCat?: string };

function clusterIconFn(palette: Record<string, Cat>) {
  return (cluster: L.MarkerCluster): L.DivIcon => {
    const counts = new Map<string, number>();
    for (const c of cluster.getAllChildMarkers()) {
      const k = (c as CatMarker)._skuCat ?? "none";
      counts.set(k, (counts.get(k) ?? 0) + 1);
    }
    // slices in palette order, so the ring reads the same way as the legend below the map
    const slices = Object.entries(palette).map(([k, cat]) => ({
      color: cat.color,
      label: cat.label.toLowerCase(),
      n: counts.get(k) ?? 0,
    }));
    return donutClusterIcon(slices, cluster.getChildCount(), COVERAGE.none.color);
  };
}

// Click popup for a store point: name + status/price and a link that opens this
// SKU's page at that store in a new tab. Built via DOM (not an HTML string) so an
// odd store id or name can't break out of the markup.
function storePopup(s: SkuStoreGeo, label: string, href: string): HTMLElement {
  const el = document.createElement("div");
  el.className = "scov__pop";

  const idLine = document.createElement("div");
  idLine.className = "scov__pop-id";
  idLine.textContent = s.name || s.store_id;
  el.appendChild(idLine);

  const meta = document.createElement("div");
  meta.className = "scov__pop-meta";
  const price = s.selling_price && s.selling_price > 0 ? ` · ₹${s.selling_price}` : "";
  const anchor = ANCHOR_LABEL[anchorKey(s)] ? ` · ${ANCHOR_LABEL[anchorKey(s)]}` : "";
  meta.textContent = `${label}${price}${anchor}`;
  el.appendChild(meta);

  const a = document.createElement("a");
  a.href = href;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  a.className = "scov__pop-link";
  a.textContent = "Open store page ↗";
  el.appendChild(a);

  return el;
}

// -- shared view sync -------------------------------------------------------
// A tiny bus that keeps sibling Leaflet maps' centre+zoom in lockstep. Each map
// registers itself; when it moves, it pushes its view to the others (guarded so
// the echoed setView doesn't loop back). Created once per SKU page and handed to
// both maps.
export type MapSync = { register: (m: L.Map) => () => void };

export function createMapSync(): MapSync {
  const maps = new Set<L.Map>();
  let applying = false;
  return {
    register(m: L.Map) {
      maps.add(m);
      const onMove = () => {
        if (applying) return;
        applying = true;
        try {
          const c = m.getCenter();
          const z = m.getZoom();
          for (const other of maps) {
            if (other !== m) other.setView(c, z, { animate: false });
          }
        } finally {
          applying = false;
        }
      };
      m.on("move zoom", onMove);
      return () => {
        m.off("move zoom", onMove);
        maps.delete(m);
      };
    },
  };
}

export function SkuStoreMap({
  platform,
  stores,
  mode,
  storeHref,
  sync,
  height = 340,
}: {
  platform: Platform;
  stores: SkuStoreGeo[];
  mode: SkuMapMode;
  // builds the "open in a new tab" target for a clicked store's point
  storeHref: (storeId: string) => string;
  // shared with the sibling map so pan/zoom stays in lockstep (optional)
  sync?: MapSync;
  // grows when the page expands the pair to fullscreen; Leaflet is re-measured on change
  height?: number;
}) {
  const elRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);

  const [showCoverage, setShowCoverage] = useState(false);

  const palette = mode === "coverage" ? COVERAGE : AVAIL;
  const keyOf = mode === "coverage" ? coverageKey : availKey;

  // stores whose serviceable-area hull the toggle can draw (carried / in-stock)
  const hullStores = useMemo(
    () => stores.filter((s) => inCoverage(s, mode)),
    [stores, mode],
  );

  // colour legend = categories actually present, in palette order
  const legend = useMemo(() => {
    const present = new Set(stores.map(keyOf));
    return Object.entries(palette).filter(([k]) => present.has(k));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stores, mode]);

  // shape legend = anchor roles actually present, in ladder order
  const shapeLegend = useMemo(() => {
    const present = new Set(stores.map(anchorKey).filter((k) => ANCHOR_LABEL[k]));
    return ANCHOR_ORDER.filter((k) => present.has(k));
  }, [stores]);

  useEffect(() => {
    if (!elRef.current || mapRef.current) return;
    const m = L.map(elRef.current, { attributionControl: false, preferCanvas: true });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(m);
    mapRef.current = m;
    const unregister = sync?.register(m);
    setTimeout(() => m.invalidateSize(), 0);
    return () => {
      unregister?.();
      m.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // markers + initial framing. Deliberately NOT keyed on showCoverage, so toggling the
  // coverage overlay never re-frames the map (fitBounds here would reset the user's zoom).
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    // chunked so building thousands of markers yields to the browser instead of
    // blocking the frame that first paints the map
    const drawn = L.markerClusterGroup({
      // tight: at country zoom a wide radius swallows the whole subcontinent into three
      // blobs, which reads as nothing. 28px keeps enough bubbles to see where the SKU is.
      maxClusterRadius: 28,
      disableClusteringAtZoom: 13,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      chunkedLoading: true,
      iconCreateFunction: clusterIconFn(palette),
    }).addTo(m);

    // Salient markers sitting above muted ones is `zIndexOffset`'s job, not insertion
    // order's -- the cluster tree owns placement. This sort only fixes the order the
    // group hands markers back in (spiderfy legs, getAllChildMarkers), so a cluster
    // expands with the carried/in-stock points listed after their grey context.
    const order = (s: SkuStoreGeo) => {
      const k = keyOf(s);
      return k === "none" || k === "dropped" ? 0 : 1;
    };
    const sorted = [...stores].sort((a, b) => order(a) - order(b));

    const markers: L.Marker[] = [];
    for (const s of sorted) {
      const key = keyOf(s);
      const cat = palette[key];
      const muted = key === "none";
      const mk = L.marker([s.latitude, s.longitude], {
        icon: shapeIcon(anchorShape(anchorKey(s)), cat.color, muted),
        zIndexOffset: muted ? 0 : 1000,
      })
        // lazy content on both, so an unopened marker costs no DOM subtree
        .bindTooltip(() => tip(s, cat.label), { direction: "top" })
        .bindPopup(() => storePopup(s, cat.label, storeHref(s.store_id)), {
          offset: [0, -6],
          minWidth: 150,
        });
      (mk as CatMarker)._skuCat = key;
      markers.push(mk);
    }
    // one bulk add: markercluster builds the whole tree once instead of re-clustering
    // per marker (the difference between a few seconds and a few tens of ms here)
    drawn.addLayers(markers);

    const pts = stores.map((s) => [s.latitude, s.longitude] as [number, number]);
    if (pts.length > 1) m.fitBounds(L.latLngBounds(pts).pad(0.12));
    else if (pts.length === 1) m.setView(pts[0], 12);
    setTimeout(() => m.invalidateSize(), 0);

    return () => {
      m.removeLayer(drawn);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stores, mode, platform, storeHref]);

  // serviceable-area hulls of the carried/in-stock stores (toggle) in their own layer so
  // flipping the toggle only adds/removes fills, no fitBounds, so the view stays put.
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const drawn = L.layerGroup().addTo(m);
    const accent = mode === "coverage" ? COVERAGE.carried.color : AVAIL.in_stock.color;

    // translucent fills overlap to build up the area where the SKU is actually available
    if (showCoverage) {
      for (const s of hullStores) {
        L.polygon(s.boundary as [number, number][], {
          color: accent,
          weight: 1.5,
          opacity: 0.9,
          fillColor: accent,
          fillOpacity: 0.28,
          interactive: false,
        }).addTo(drawn);
      }
    }

    return () => {
      m.removeLayer(drawn);
    };
  }, [showCoverage, hullStores, mode]);

  // Leaflet caches the container size, so a height change (fullscreen in/out) needs an
  // explicit re-measure or the tiles stay laid out for the old box.
  useEffect(() => {
    const m = mapRef.current;
    if (m) setTimeout(() => m.invalidateSize(), 60);
  }, [height]);

  if (stores.length === 0) {
    return (
      <div className="muted" style={{ padding: 24, textAlign: "center" }}>
        No mappable stores: run a sweep, or this platform has no geolocated stores yet.
      </div>
    );
  }

  const coverNoun = mode === "coverage" ? "carried-store" : "in-stock";
  return (
    <div className="sdetail__mapwrap">
      <div className="row" style={{ alignItems: "center", gap: 8 }}>
        <label
          className="row"
          style={{
            gap: 6,
            alignItems: "center",
            fontSize: 11.5,
            cursor: hullStores.length ? "pointer" : "not-allowed",
            opacity: hullStores.length ? 1 : 0.5,
            userSelect: "none",
          }}
          title={
            hullStores.length
              ? `Draw the serviceable area of the ${coverNoun} stores`
              : "No serviceable-area hulls available for these stores yet"
          }
        >
          <input
            type="checkbox"
            checked={showCoverage}
            disabled={!hullStores.length}
            onChange={(e) => setShowCoverage(e.target.checked)}
          />
          Show {coverNoun} coverage area
          {hullStores.length > 0 && (
            <span className="faint">({hullStores.length})</span>
          )}
        </label>
      </div>
      <div ref={elRef} style={{ width: "100%", height, borderRadius: 6, overflow: "hidden", zIndex: 0 }} />
      <div className="sdetail__legend">
        {legend.map(([k, c]) => (
          <span className="sdetail__legend-item" key={k}>
            <span className="sdetail__legend-dot" style={{ background: c.color }} />
            {c.label}
          </span>
        ))}
      </div>
      {shapeLegend.length > 0 && (
        <div className="sdetail__legend">
          <span className="faint" style={{ fontSize: 10.5 }}>Anchor role:</span>
          {shapeLegend.map((k) => (
            <span className="sdetail__legend-item" key={k}>
              <ShapeGlyph shape={anchorShape(k)} />
              {ANCHOR_LABEL[k]}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Small neutral-ink glyph mirroring a marker shape, for the shape legend.
function ShapeGlyph({ shape }: { shape: ShapeKind }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      style={{ fill: "var(--ink-2)", verticalAlign: "-1px" }}
      dangerouslySetInnerHTML={{ __html: shapeInner(shape) }}
    />
  );
}

function tip(s: SkuStoreGeo, label: string): string {
  const price = s.selling_price && s.selling_price > 0 ? ` · ₹${s.selling_price}` : "";
  return `${s.name || s.store_id}, ${label}${price}`;
}
